import { Cookie } from '../../utils/cookie';

import { AnalyticsState } from './analytics-state';
import { ATL_COHORT, Cohort } from './cohort';
import { Helpers } from './helpers';
import { Tracking } from './tracking';

const TRELLO_USER_ID = 'token';
const XID = 'seg_xid';
const pageViewProperties: Record<string, unknown> = {};
const MARKETO_ID = '_mkto_trk';
const REFERRER_TRELLO_DOMAINS = ['trello.com', 'trellis.coffee'];

export class Identity {
	/**
	 * Sends a track event with known identifiers
	 * @function identify
	 * @param {string} userId - required
	 * @param {object} traits - optional, dictionary of custom traits to send
	 */
	public static identify(userId: string | null, traits: Record<string, unknown>) {
		traits = !!traits && typeof traits === 'object' ? traits : {};
		const data = traits;
		data['atlassian_id'] = userId;
		data['atlJSVersion'] = AnalyticsState.version || '1.0.0';

		try {
			data['ga_clientid'] = Cookie.readCookie('_ga');
		} catch (e) {
			Helpers.sendWrapperLoadError('Error reading _ga cookie');
		}

		Tracking.trackEvent('known_identifiers', data);
	}

	/**
	 * used internally to gather all known ids and call identify
	 * @function autoIdentify
	 */
	public static autoIdentify() {
		const emailDetected = Helpers.emailAddressIsDetected();

		const userId = Identity.getAtlassianUserId();

		const xid = Cookie.readCookie(XID);

		if (emailDetected) {
			const integrations = {
				All: false,
				'Google Analytics': true,
			};

			if (!AnalyticsState.pageViewOptions) {
				AnalyticsState.pageViewOptions = {
					integrations,
				};
			} else {
				AnalyticsState.pageViewOptions['integrations'] = integrations;
			}

			if (Helpers.urlContainsEmail(window.location.href)) {
				Helpers.addCleanedUrlAndPaths(pageViewProperties);
			}
			if (Helpers.urlContainsEmail(document.referrer)) {
				Helpers.addCleanedReferrer(pageViewProperties);
			}
		}

		try {
			pageViewProperties['anonymous_id'] = AnalyticsState.anonymousId;
		} catch (e) {
			Helpers.sendWrapperLoadError('Error reading segment anonymousId');
		}

		try {
			pageViewProperties[ATL_COHORT] = Cohort.readWriteAtlCohort();
		} catch (e) {
			Helpers.sendWrapperLoadError('Error read / writing atlCohort cookie');
		}

		if (userId || xid) {
			Identity.identify(userId, pageViewProperties);
		}
	}

	public static getAtlassianUserId() {
		const userId = Cookie.readCookie('__aid_user_id');
		if (userId) {
			return decodeURIComponent(userId);
		}
		return null;
	}

	public static getTrelloUserId() {
		try {
			const page = location.href ? new URL(location.href) : null;
			// since the trello cookie is such a common name. check to make sure we're actually on a trello page
			if (
				page &&
				(page.hostname.includes('trello.com') || page.hostname.includes('trellis.coffee'))
			) {
				const token = Cookie.readCookie(TRELLO_USER_ID);
				if (token) {
					return decodeURIComponent(token).split('/')[0];
				}
			}
		} catch (e) {
			Helpers.sendWrapperLoadError('Error reading Trello userId');
		}
		return null;
	}

	public static getMarketoId() {
		const marketoId = Cookie.readCookie(MARKETO_ID);
		if (marketoId) {
			return decodeURIComponent(marketoId);
		}
		return null;
	}

	public static getMarketoIdPromise(enableRetry = true) {
		const waitCount = 0;
		const maxWait = enableRetry ? 5000 : 0;
		return new Promise<string>((resolve) => {
			Identity._internalMarketoPromise(waitCount, maxWait, resolve);
		});
	}

	public static _internalMarketoPromise(
		waitCount: number,
		maxWait: number,
		resolve: (marektoId: string) => void,
	) {
		const delay = 500;
		const marketoId = Cookie.readCookie(MARKETO_ID);

		// wait for up to 5 seconds for the marketo cookie to show up
		if (marketoId) {
			resolve(decodeURIComponent(marketoId));
		} else {
			waitCount += delay;
			if (waitCount >= maxWait) {
				Helpers.sendWrapperLoadError('getMarketoIdPromise error');
				resolve('');
			} else {
				setTimeout(function () {
					Identity._internalMarketoPromise(waitCount, maxWait, resolve);
				}, delay);
			}
		}
	}

	public static isReferrerTrello() {
		if (!document.referrer) {
			return false;
		}

		const ref = new URL(document.referrer);
		return REFERRER_TRELLO_DOMAINS.includes(ref.hostname);
	}
}
